import type { CategoryModel, DocumentRef, EventModel, MaterialModel, MaterialSummary, NewsModel } from '@site/shared'
import { mapMedia } from './media'

type Raw = Record<string, unknown>
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)
const obj = (v: unknown) => (v && typeof v === 'object' ? (v as Raw) : undefined)

const absolute = (url: string, baseUrl: string) => (url.startsWith('http') ? url : `${baseUrl}${url}`)

export function mapCategory(raw: unknown): CategoryModel | undefined {
  const c = obj(raw)
  if (!c || !c.slug) return undefined
  return { id: String(c.id), slug: String(c.slug), name: String(c.name ?? ''), description: str(c.description) }
}

export function mapDocument(raw: unknown, baseUrl: string): DocumentRef | undefined {
  const d = obj(raw)
  const url = str(d?.url)
  if (!d || !url) return undefined
  return {
    id: String(d.id),
    title: str(d.title) ?? str(d.filename) ?? '',
    url: absolute(url, baseUrl),
    mimeType: str(d.mimeType),
    filesize: typeof d.filesize === 'number' ? d.filesize : undefined,
  }
}

export function mapMaterialSummary(raw: Raw, baseUrl: string): MaterialSummary {
  return {
    id: String(raw.id),
    slug: String(raw.slug),
    title: String(raw.title ?? ''),
    summary: str(raw.summary),
    category: mapCategory(raw.category),
    cover: mapMedia(obj(raw.cover), baseUrl),
    featured: raw.featured === true,
    updatedAt: str(raw.updatedAt),
  }
}

export function mapMaterial(raw: Raw, baseUrl: string): MaterialModel {
  const attachments = Array.isArray(raw.attachments) ? raw.attachments : []
  return {
    ...mapMaterialSummary(raw, baseUrl),
    body: raw.body ?? undefined,
    attachments: attachments.flatMap((a) => mapDocument(a, baseUrl) ?? []),
    reviewedAt: str(raw.reviewedAt),
  }
}

export function mapEvent(raw: Raw): EventModel {
  return {
    id: String(raw.id),
    title: String(raw.title ?? ''),
    startsAt: String(raw.startsAt),
    endsAt: str(raw.endsAt),
    location: str(raw.location),
    summary: str(raw.summary),
    signupUrl: str(raw.signupUrl),
  }
}

export function mapNews(raw: Raw): NewsModel {
  return { id: String(raw.id), title: String(raw.title ?? ''), publishedAt: String(raw.publishedAt), excerpt: str(raw.excerpt) }
}
